import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { BirthdayInput } from '@/core/components';
import { getRunnerByBib, verifyIdentity } from '@/core/services/claim';
import type { Runner } from '@/core/types';
import { getFinishTime } from '@/core/types';

interface IdentityVerificationStepProps {
  raceId: string;
  bib: string;
  onSuccess: (runner: Runner) => void;
  onBack: () => void;
}

export function IdentityVerificationStep({ raceId, bib, onSuccess, onBack }: IdentityVerificationStepProps) {
  const theme = useTheme();
  const gradient = theme.custom?.gradient || theme.palette.primary.main;

  const [email, setEmail] = useState('');
  const [birthday, setBirthday] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !birthday) return;

    setLoading(true);
    setError(null);

    try {
      // 本人確認
      const verifyResult = await verifyIdentity(raceId, email.trim(), birthday);

      if (!verifyResult.success) {
        setError(verifyResult.error || '本人確認に失敗しました');
        return;
      }

      // ゼッケン番号の照合
      if (verifyResult.bib !== bib) {
        setError('入力された情報がゼッケン番号と一致しません');
        return;
      }

      // 完走結果を取得
      const runner = await getRunnerByBib(raceId, bib);

      if (!runner) {
        setError('完走記録が見つかりません');
        return;
      }

      if (!getFinishTime(runner)) {
        setError('完走記録がまだ登録されていません');
        return;
      }

      onSuccess(runner);
    } catch (err) {
      setError('エラーが発生しました。もう一度お試しください');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <VerifiedUserIcon
          sx={{
            color: theme.palette.primary.main,
            fontSize: 28,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            background: gradient,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          本人確認
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        ゼッケン番号: <strong>{bib}</strong>
        <br />
        エントリー時に登録したメールアドレスと生年月日を入力してください。
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        type="email"
        label="メールアドレス"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="例: taro@example.com"
        disabled={loading}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused': {
              boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
            },
          },
        }}
      />

      <Box sx={{ mb: 4 }}>
        <BirthdayInput value={birthday} onChange={setBirthday} disabled={loading} />
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={onBack}
          disabled={loading}
          sx={{
            flex: 1,
            height: 48,
            position: 'relative',
            border: 'none',
            background: theme.palette.background.paper,
            color: theme.palette.text.primary,
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: 'inherit',
              padding: '2px',
              background: gradient,
              WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
              pointerEvents: 'none',
            },
            '&:hover': {
              background: theme.palette.action.hover,
            },
            '&:disabled': {
              color: theme.palette.action.disabled,
              '&::before': {
                background: theme.palette.action.disabledBackground,
              },
            },
          }}
        >
          戻る
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading || !email.trim() || !birthday}
          sx={{
            flex: 2,
            background: gradient,
            color: '#fff',
            height: 48,
            fontWeight: 600,
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.15)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            '&:hover': {
              background: gradient,
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)',
            },
            '&:disabled': {
              background: theme.palette.action.disabledBackground,
            },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : '確認'}
        </Button>
      </Box>
    </Box>
  );
}
